<template>
  <tr class="card">
    <td class="card__status">
      <p>
        <span :class="`statusMark__${approvalStatus}`" class="statusMark" />
        {{ approvalStatusMessage }}
      </p>
      <p>{{ lastEditedDateTime }}</p>
      <p>{{ approvalLatestStatusMessage }}</p>
      <p>{{ applicant }}</p>
      <p>{{ author }}</p>
    </td>
    <td class="card__content">
      <div class="card__content__image" :class="{ noContent: !postImages || postImages.length === 0 }">
        <div>
          <img v-if="postImages.length !== 0" :src="postImages[0].url">
          <p v-else class="noimage">NO IMAGE</p>
        </div>
      </div>
      <div class="card__content__detail">
        <div class="upperRow">
          <span class="topic-type">
            {{ topicTypes[topicType] }}
          </span>
          <KkCategoryTip :name="categoryName" :is-editable="false" />
        </div>

        <KkPostTitleText :topic-type="topicType" :post-title="postTitle" :post-event-title="event?.title" />

        <p v-if="summary" class="summary"><KkMore :text="summary" :length="moreDisplayLength" :is-expand-html="false" /></p>

        <p v-if="couponCode" class="coupon-code">
          クーポンコード：<span>{{ couponCode }}</span>
        </p>

        <p v-if="event" class="event-period">
          {{ topicType === 'EVENT' ? '期間' : '有効期限' }}：{{ eventPeriod([event.startedAt, event.endedAt]) }}
        </p>

        <span
          v-if="callToAction"
          class="link"
        >
          {{ selectActionType(callToAction.actionType) }}
        </span>
      </div>
    </td>
    <td class="card__setting">
      <div class="scheduled">
        {{ $t('pages.CompaniesStorePost.scheduledAt') }}：<time class="scheduled__at">{{ scheduledDateTime }}</time>
      </div>
      <div class="store">
        <button @click="openPostStoresModal">投稿店舗：<span>{{ storeCount.all }}店舗</span></button>
      </div>
      <div v-if="scheduledAt && repeatConfiguration" class="c-info repeatingPost">
        <p class="c-info__content">{{ $t('pages.CompaniesStorePost.repeatingPost') }}：<span>{{ repeatingPostName }}</span></p>
      </div>

      <!-- サービス一覧 -->
      <div v-if="services.length" class="services">
        <ul class="services__group">
          <li
            v-for="service in services"
            :key="service"
            class="services__item"
          >
            <KkSiteIcon :name="service" />
          </li>
        </ul>
      </div>
    </td>
    <td class="card__option">
      <div class="card__action c-button c-button--icon">
        <router-link v-if="canEdit" :to="routeParameterEdit" class="c-button__item">
          <KkIcon
            name="edit"
            color="rgba(58, 59, 63, 0.7)"
            class="c-button__icon"
          />
          編集する
        </router-link>
        <button v-if="canApprove" class="c-button__item" @click="openActionModal">
          <KkIcon
            name="detail"
            color="rgba(58, 59, 63, 0.7)"
            class="c-button__icon"
          />
          申請内容の確認
        </button>
        <button v-if="!canApprove" class="c-button__item" @click="openPreviewModal">
          <KkIcon
            name="detail"
            color="rgba(58, 59, 63, 0.7)"
            class="c-button__icon"
          />
          プレビュー
        </button>
        <router-link
          v-if="canEdit"
          :to="routeParameterCopy"
          class="c-button__item"
        >
          <KkIcon
            name="copy"
            color="rgba(58, 59, 63, 0.7)"
            class="c-button__icon"
          />
          複製する
        </router-link>

        <button v-if="canDelete" class="c-button__item" @click="deletePost(id)">
          <KkIcon
            name="delete"
            color="rgba(58, 59, 63, 0.7)"
            class="c-button__icon"
          />
          削除する
        </button>

        <button v-if="canCancelRequestApproving" class="c-button__item" @click="deleteRequestApprovingPost(id)">
          <KkIcon
            name="delete"
            color="rgba(58, 59, 63, 0.7)"
            class="c-button__icon"
          />
          申請取り消し
        </button>
      </div>
    </td>
  </tr>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import moment from 'moment'
import { i18n } from '../../plugins/i18n'
import { dayOfWeek } from '../../modules/dayOfWeekConst'
import { KkIcon, KkSiteIcon } from '../../components/base'
import { KkCategoryTip } from '../domains/sns'
import KkMore from '../base/KkMore/index.vue'
import { KkPostTitleText } from '../../domains/post'

export default {
  name: 'CompaniesStorePostsApprovalTableRow',
  components: {
    KkIcon,
    KkSiteIcon,
    KkCategoryTip,
    KkMore,
    KkPostTitleText
  },
  props: {
    id: Number,
    storeCount: {
      type: Object,
      required: true
    },
    approvalStatus: {
      type: String,
      required: true
    },
    lastEditedAt: {
      type: String,
      required: true
    },
    authorName: {
      type: String,
      required: true
    },
    applicantName: {
      type: String,
      default: ''
    },
    editor: {
      type: String,
      required: true
    },
    postImages: {
      type: Array,
      default: () => {
        return []
      }
    },
    topicType: {
      type: String,
      required: true
    },
    summary: String,
    couponCode: String,
    postTitle: String,
    event: Object,
    callToAction: Object,
    scheduledAt: {
      type: String,
      default: null,
    },
    repeatConfiguration: {
      type: String,
      default: null
    },
    editable: {
      type: Boolean,
      default: false,
    },
    categoryName: {
      type: String,
      default: ''
    }
  },
  emits: ['delete-approval-post', 'post-approval-post', 'delete-request-approval-post', 'open-post-stores-modal', 'open-preview-action-modal'],
  data() {
    return {
      showPreviewModal: false,
      showActionModal: false,
      moreDisplayLength: 40,
    }
  },
  computed: {
    ...mapState({
      post: state => state.companiesStorePosts.post,
      reservationDateTime: state => state.companiesStorePosts.reservationDateTime,
    }),
    ...mapGetters({
      topicTypes: 'companiesStorePosts/topicTypes',
      selectActionType: 'companiesStorePosts/selectActionType',
      showOnlyOwnerAuthority: 'companiesAccessibleApi/showOnlyOwnerAuthority',
      showMoreThanOperatorAuthority: 'companiesAccessibleApi/showMoreThanOperatorAuthority',
      showOnlyEditorOrOperatorAuthority: 'companiesAccessibleApi/showOnlyEditorOrOperatorAuthority'
    }),
    approvalStatusMessage() {
      return i18n.t('pages.CompaniesStorePost.index.approvalStatus.' + this.approvalStatus)
    },
    approvalLatestStatusMessage() {
      if(this.approvalStatus === 'rejected' && !this.editor) return  i18n.t('pages.CompaniesStorePost.index.approvalLatestStatus.rejectedPassedSchedule')

      return i18n.t('pages.CompaniesStorePost.index.approvalLatestStatus.' + this.approvalStatus, { editor: this.editor })
    },
    author() {
      return `(${this.$t('pages.CompaniesStorePost.index.authorName', { author: this.authorName })})`
    },
    applicant() {
      if(this.approvalStatus === 'rejected' && this.applicantName) {
        return `(${this.$t('pages.CompaniesStorePost.index.applicantName', { applicant: this.applicantName })})`
      }
      return null
    },
    scheduledMoment() {
      return moment(this.scheduledAt)
    },
    scheduledDateTime() {
      if(!this.scheduledAt) {
        return '-'
      }
      const dayOfWeek = this.dayOfWeekOfReservationDate.charAt(0)
      return this.scheduledMoment.format(`${i18n.t('format.date.default')}(${dayOfWeek}) ${i18n.t('format.time.default')}`)
    },
    dayOfWeekOfReservationDate() {
      const dowNumber = this.scheduledMoment.day()
      return Object.values(dayOfWeek)[dowNumber]
    },
    dayOfWeekCountOfReservationDate() {
      return Math.floor((this.scheduledMoment.toDate().getDate() - 1) / 7) + 1
    },
    repeatingPostName() {
      switch (this.repeatConfiguration) {
      case 'daily':
        return '毎日'
      case 'weekly':
        return `毎週 ${this.dayOfWeekOfReservationDate}`
      case 'wdayIndex':
        return `毎月 第${this.dayOfWeekCountOfReservationDate}${this.dayOfWeekOfReservationDate}`
      case 'yearly':
        return `毎年 ${this.scheduledMoment.format('M月D日')}`
      case 'weekday':
        return '毎週 (月〜金)'
      case 'monthly':
        return `毎月 ${this.scheduledMoment.format('D日')}`
      }
      return null
    },
    currentCompanyUuid() {
      return this.$route.params.company_uuid
    },
    lastEditedDateTime() {
      return moment(this.lastEditedAt).format(i18n.t('format.datetime.default'))
    },
    canApprove() {
      return this.showOnlyOwnerAuthority && this.approvalStatus === 'requesting'
    },
    canEdit() {
      if(!this.showMoreThanOperatorAuthority) {
        return false
      }

      // 承認待ちの状態 && オーナーじゃない 場合は編集できない
      if (this.approvalStatus === 'requesting' && !this.showOnlyOwnerAuthority) {
        return false
      }

      return this.editable
    },
    canDelete() {
      if(this.showOnlyOwnerAuthority) {
        return true
      }
      if(!this.showMoreThanOperatorAuthority) {
        return false
      }

      if (['draft', 'rejected'].includes(this.approvalStatus)) {
        return this.editable
      }

      const isAfter = this.scheduledMoment.isAfter(moment())
      const isRequesting = this.approvalStatus === 'requesting'

      // 投稿予約時間までは編集OK && 承認待ちの状態で編集できない && 操作可能な店舗が含まれる
      return isAfter && !isRequesting && this.editable
    },
    canCancelRequestApproving() {
      const isAfter = this.scheduledMoment.isAfter(moment())
      return isAfter && this.showOnlyEditorOrOperatorAuthority && this.approvalStatus === 'requesting' && this.editable
    },
    routeParameterEdit() {
      if(this.approvalStatus === 'draft') {
        return { name: 'CompaniesStoreDraftedPostsEdit', params: { company_uuid: this.currentCompanyUuid, drafted_post_id: this.id } }
      }

      if(this.approvalStatus === 'rejected' || this.approvalStatus === 'requesting') {
        return { name: 'CompaniesStoreRequestingPostsEdit', params: { company_uuid: this.currentCompanyUuid, requesting_post_id: this.id } }
      }

      return { name: 'CompaniesStorePostsEdit', params: { company_uuid: this.currentCompanyUuid, post_id: this.id } }
    },
    routeParameterCopy() {
      if (this.approvalStatus === 'draft') {
        return { name: 'CompaniesStorePostsNew', params: { company_uuid: this.currentCompanyUuid }, query: { drafted_post_copy: this.id } }
      }

      if (this.approvalStatus === 'rejected' || this.approvalStatus === 'requesting') {
        return { name: 'CompaniesStorePostsNew', params: { company_uuid: this.currentCompanyUuid }, query: { requesting_post_copy: this.id } }
      }

      return { name: 'CompaniesStorePostsNew', params: { company_uuid: this.currentCompanyUuid }, query: { copy: this.id } }
    },
    services() {
      const services = []
      if (this.storeCount.googleMap > 0) services.push('google')
      if (this.storeCount.docomo > 0) services.push('docomo')
      if (this.storeCount.yahooMap > 0) services.push('yahoo_map')

      return services
    }
  },
  methods: {
    ...mapActions({
      createPreview: 'companiesStorePosts/createPreview',
      createRequestingPostPreview: 'companiesStorePosts/createRequestingPostPreview',
      getDraftedPost: 'companiesStorePosts/readDraftedPost',
      getRequestingPost: 'companiesStorePosts/getRequestingPost',
      getPost: 'companiesStorePosts/getPost',
      updateSubmitActionModalType: 'companiesStorePosts/updateSubmitActionModalType',
      updateRequestApproval: 'companiesStorePosts/updateApprovalRequest',
    }),
    eventPeriod(period) {
      const startedAt = moment(new Date(period[0])).format(i18n.t('format.datetime.default'))
      const endedAt = moment(new Date(period[1])).format(i18n.t('format.datetime.default'))

      return startedAt + ' - ' + endedAt
    },
    async openActionModal() {
      this.updateSubmitActionModalType('approve')
      this.$emit('open-preview-action-modal', { postId: this.id, onlyPreview: false })
    },
    async openPreviewModal() {
      if(this.approvalStatus === 'draft'){
        this.updateSubmitActionModalType('draft')
      } else {
        this.updateSubmitActionModalType('')
      }
      this.$emit('open-preview-action-modal', { postId: this.id })
    },
    async deletePost(id){
      this.$emit('delete-approval-post', { id: id, status: this.approvalStatus} )
    },
    async deleteRequestApprovingPost(id){
      this.$emit('delete-request-approval-post', { id: id, status: this.approvalStatus} )
    },
    openPostStoresModal() {
      this.$emit('open-post-stores-modal', this.id)
    },
  }
}
</script>

<style lang="scss" scoped>
@import '../../../../stylesheets/biz_spa/foundation/_var.scss';

.card {
  @media screen and (max-width: 768px) {
    flex-direction: column;
  }

  &__status {
    font-size: 12px;
    p:not(:first-child) {
      margin-top: 8px;
    }
  }

  &__content {
    display: flex;

    &__image {
      flex: none;
      margin-right: 24px;
      margin-bottom: 16px;
      width: 40%;
      height: 120px;
      border: 1px solid #E4E7EB;
      border-radius: 4px;

      @media screen and (max-width: 768px) {
        width: 100%;
        margin-right: 0;
      }

      &.noContent {
        display: flex;
        justify-content: center;
        align-items: center;

        @media screen and (max-width: 768px) {
          display: none;
        }

        div {
          height: unset;
          width: unset;
        }
      }

      div {
        height: 100%;
        width: 100%;
      }

      img {
        display: block;
        object-fit: contain;
        width: 100%;
        height: 100%;
      }

      .noimage {
        color: #979797;
        letter-spacing: 3px;
      }
    }
    &__detail {
      .topic-type {
        color: white;
        font-weight: bold;
        font-size: 12px;

        border-radius: 12px;
        background-color: #3A3B3F;
        padding: 4px 6px;
        margin-right: 5px;
      }
      .upperRow {
        display: flex;
        align-items: center;
      }
      .title {
        margin-bottom: 0;
        font-size: 14px;
        word-break: break-all;
      }

      .summary {
        white-space: pre-wrap;
        word-break: break-all;
      }

      .coupon-code,
      .event-period {
        font-size: 12px;
        margin-top: 8px;
      }

      .coupon-code {
        span {
          font-weight: bold;
          word-break: break-all;
        }
      }

      .link {
        display: inline-block;
        margin-top: 10px;
        padding: 8px 16px;
        color: #3A3B3F;
        font-size: 12px;
        line-height: 1.17;
        background-color: #EEE;
        border-radius: 4px;
        text-decoration: none;

        @media screen and (max-width: 768px) {
          margin-top: 8px;
        }
      }
    }
  }
  &__setting {
    vertical-align: top;
    font-size: 12px;

    .scheduled {
      width: 200px;
    }

    .store {
      margin: 4px 0 10px;
      font-size: 12px;
      line-height: 1.17;

      span {
        color: #E9446A;
        font-weight: bold;
        text-decoration: underline;
      }
    }

    .services {
      margin-top: 8px;

      &__group {
        display: flex;
        flex-wrap: wrap;
      }

      &__item {
        width: 38px;
        height: 38px;
        padding: 4px;
        margin: 0 4px 4px 0;
        border: 1px solid rgba(228,231,235,.5);
        border-radius: 8px;
      }
    }
  }
  &__option {
    border-right: 1px solid #E4E7EB;
    border-radius: 4px;
  }
  &__action {
    flex-wrap: wrap;
    align-items: center;
    margin: 0 -8px -8px;
  }
}

.repeatingPost {
  font-weight: bold;
  color: #FFFFFF;
  background-color: #3A3B3F;
  margin-bottom: 0px;
}

.statusMark {
  border-radius: 50%;
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 2px;
  &__rejected {
    background: $color-accent-primary;
  }
  &__requesting {
    background: $complete-color;
  }
  &__draft {
    background: #D2D5DB;
  }
}

tr, th, td {
  @media screen and (max-width: 767px) {
    display: block !important;
  }
}

tr {
  @media screen and (max-width: 767px) {
    margin-bottom: 8px;
    padding: 16px;
    border: 1px solid #E4E7EB;
    border-radius: 4px;
  }
}

td {
  border-bottom: 1px solid #E4E7EB;
  &:not(:first-child) {
    border-left: none;
  }
  @media screen and (max-width: 767px) {
    padding: 0 !important;
    border: 0 !important;
  }
}

.card {
  @media screen and (max-width: 767px) {
    &__content {
      display: block;
      margin-top: 16px;
    }
    &__setting {
      margin-top: 16px;
    }
    &__option {
      margin-top: 16px;
    }
  }
}
:deep(.category) {
  font-size: 12px;
  margin-bottom: 0;
}
</style>
